import {AnimatePresence, motion} from 'framer-motion';
import {
  DeclinedUser,
  getUserSubheader,
  HubblAttendee,
  InvitedUser,
  Palette,
  SimpleUser,
  User,
} from 'hubbl-shared';
import moment from 'moment';
import {memo, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {AvatarStorageRef, PrimaryButton, TextHeading5} from '..';
import {UserDTO} from '../../models';
import {CloseButton} from '../buttons/close-button/CloseButton';
import {IconButton} from '../buttons/icon-button/IconButton';
import {TextHeading2} from '../text/TextHeading2';
import {useI18n} from '../../hooks';
import {ConfirmationModal} from './ConfirmationModal';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContent = styled(motion.div)`
  background: ${Palette.white};
  border-radius: 16px;
  width: 100%;
  max-width: min(95vw, 1200px);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  margin: 20px;
`;

const Header = styled.div`
  padding: 24px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  position: relative;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${Palette.almostBlack750};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${Palette.almostBlack500};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${Palette.almostBlack};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${Palette.primarySubtleGreen};
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  color: ${Palette.almostBlack};
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
`;

const TableBody = styled.tbody`
  tr {
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: ${Palette.primaryCherokee}20;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
      background-color: ${Palette.primaryCherokee}30;
    }
  }
`;

const TableCell = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  tr:hover &::after {
    opacity: 0.6;
  }
`;

interface UserListModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  users:
    | HubblAttendee[]
    | DeclinedUser[]
    | UserDTO[]
    | (InvitedUser | UserDTO | User)[];
  checkIns?: Record<string, {checkedInAt: string}>;
  isPublic?: boolean;
  showOrganizationalInfo?: boolean;
  showCalendarIntegration?: boolean;
  compactView?: boolean;
  onUserAction?: (user: User | InvitedUser, action: string) => void;
  actionLabel?: string;
  secondaryActionLabel?: string;
  onSecondaryUserAction?: (user: User | InvitedUser, action: string) => void;
}

export const UserListModal = memo(
  ({
    isOpen,
    onClose,
    title,
    users,
    checkIns,
    isPublic,
    showOrganizationalInfo = false,
    showCalendarIntegration = false,
    onUserAction,
    actionLabel,
    secondaryActionLabel,
    onSecondaryUserAction,
  }: UserListModalProps) => {
    const navigate = useNavigate();
    const t = useI18n();
    const [pendingAction, setPendingAction] = useState<{
      user: User | InvitedUser;
      action: 'decline' | 'attend';
    } | null>(null);

    // Clear pending action when modal closes
    useEffect(() => {
      if (!isOpen) {
        setPendingAction(null);
      }
    }, [isOpen]);

    const handleConfirmAction = () => {
      if (!pendingAction) return;

      if (pendingAction.action === 'decline') {
        onUserAction?.(pendingAction.user, 'decline');
      } else {
        onSecondaryUserAction?.(pendingAction.user, 'attend');
      }

      setPendingAction(null);
    };

    const handleCancelAction = () => {
      setPendingAction(null);
    };

    if (!isOpen) return null;

    const isDeclinedUsers = users.length > 0 && 'declinedAt' in users[0];
    const showCheckIn = !!checkIns;
    const showActions = !!onUserAction || !!onSecondaryUserAction;

    const handleUserClick = (userId: string) => {
      onClose(); // Close the modal first
      navigate(`/employee/user/${userId}`);
    };

    return (
      <AnimatePresence>
        <ModalOverlay
          onClick={onClose}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.2, ease: 'easeInOut'}}>
          <ModalContent
            initial={{y: 20, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            exit={{y: -20, opacity: 0}}
            transition={{duration: 0.2, ease: 'easeInOut'}}
            whileHover={{
              scale: 1.01,
              transition: {duration: 0.2, ease: 'easeInOut'},
            }}
            onClick={e => e.stopPropagation()}>
            <CloseButton onClick={onClose} />
            <Header>
              <TextHeading2 text={title} />
            </Header>
            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>
                      <TextHeading5 text={t.common.user} />
                    </TableHeaderCell>
                    {!isPublic && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.common.email} />
                      </TableHeaderCell>
                    )}
                    {showCheckIn && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.events.checkedInAt} />
                      </TableHeaderCell>
                    )}
                    {isDeclinedUsers && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.events.declinedAt} />
                      </TableHeaderCell>
                    )}
                    {isPublic && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.events.extraInfo} />
                      </TableHeaderCell>
                    )}
                    {showOrganizationalInfo && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.events.department} />
                      </TableHeaderCell>
                    )}
                    {showOrganizationalInfo && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.events.company} />
                      </TableHeaderCell>
                    )}
                    {showOrganizationalInfo && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.common.location} />
                      </TableHeaderCell>
                    )}
                    {showCalendarIntegration && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.scenes.table.calendarIntegration} />
                      </TableHeaderCell>
                    )}
                    {showActions && (
                      <TableHeaderCell>
                        <TextHeading5 text={t.common.actions} />
                      </TableHeaderCell>
                    )}
                  </tr>
                </TableHeader>
                <TableBody>
                  {users.map(user => {
                    // Handle InvitedUser specially - they cannot be moved around
                    if ('invitedAt' in user) {
                      return (
                        <tr key={user.email} style={{opacity: 0.7}}>
                          <TableCell>
                            <UserInfo>
                              <div
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: Palette.primarySubtleGreen,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: Palette.white,
                                }}>
                                ?
                              </div>
                              <div>
                                <TextHeading5 text="Pending Invitation" />
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: Palette.almostBlack500,
                                    marginTop: '2px',
                                  }}>
                                  Invited {moment(user.invitedAt).fromNow()}
                                </div>
                              </div>
                            </UserInfo>
                          </TableCell>
                          {!isPublic && (
                            <TableCell>
                              <TextHeading5 text={user.email} />
                            </TableCell>
                          )}
                          {showCheckIn && (
                            <TableCell>
                              <TextHeading5 text="-" />
                            </TableCell>
                          )}
                          {isDeclinedUsers && (
                            <TableCell>
                              <TextHeading5 text="-" />
                            </TableCell>
                          )}
                          {isPublic && (
                            <TableCell>
                              <TextHeading5 text="-" />
                            </TableCell>
                          )}
                          {showOrganizationalInfo && (
                            <TableCell>
                              <TextHeading5 text="-" />
                            </TableCell>
                          )}
                          {showCalendarIntegration && (
                            <TableCell>
                              <TextHeading5 text="-" />
                            </TableCell>
                          )}
                          {/* No action buttons for InvitedUsers - they're not real users yet */}
                          {showActions && <TableCell />}
                        </tr>
                      );
                    }

                    // Handle regular users (HubblAttendee, DeclinedUser, UserDTO, SimpleUser)
                    let displayUser:
                      | HubblAttendee
                      | DeclinedUser
                      | UserDTO
                      | SimpleUser
                      | undefined;

                    if ('declinedAt' in user) {
                      displayUser = user.user;
                    } else {
                      displayUser = user;
                    }

                    if (!displayUser) return null;

                    const id = displayUser.id ?? '';
                    const checkInTime = checkIns?.[id]?.checkedInAt;
                    return (
                      <tr
                        key={id}
                        onClick={() => handleUserClick(id)}
                        title={t.events.clickToViewUserProfile}>
                        <TableCell>
                          <UserInfo>
                            <AvatarStorageRef
                              name={displayUser.fullName}
                              imageRef={displayUser.photoUrl}
                            />
                            <TextHeading5 text={displayUser.fullName} />
                          </UserInfo>
                        </TableCell>
                        {!isPublic && (
                          <TableCell>
                            <TextHeading5 text={displayUser.email} />
                          </TableCell>
                        )}
                        {showCheckIn && (
                          <TableCell>
                            <TextHeading5
                              text={
                                checkInTime
                                  ? moment(checkInTime).format('HH:mm:ss')
                                  : ''
                              }
                            />
                          </TableCell>
                        )}
                        {isDeclinedUsers && 'declinedAt' in user && (
                          <TableCell>
                            <TextHeading5
                              text={moment(user.declinedAt).format(
                                'DD. MMM HH:mm',
                              )}
                            />
                          </TableCell>
                        )}
                        {isPublic && 'funFact' in displayUser && (
                          <TableCell>
                            <TextHeading5
                              text={getUserSubheader(
                                displayUser,
                                displayUser.settings.preferredLanguage,
                              )}
                            />
                          </TableCell>
                        )}
                        {showOrganizationalInfo && (
                          <TableCell>
                            <TextHeading5
                              text={
                                displayUser.department?.label?.en ||
                                displayUser.department?.label?.da ||
                                t.user.noDepartment
                              }
                            />
                          </TableCell>
                        )}

                        {showOrganizationalInfo && (
                          <TableCell>
                            <TextHeading5
                              text={
                                ('customSSOProperties' in displayUser &&
                                  displayUser.customSSOProperties
                                    ?.companyname) ||
                                t.user.noSubCompany
                              }
                            />
                          </TableCell>
                        )}
                        {showOrganizationalInfo && (
                          <TableCell>
                            <TextHeading5
                              text={
                                ('customSSOProperties' in displayUser &&
                                  displayUser.customSSOProperties
                                    ?.officelocation) ||
                                t.user.noLocation
                              }
                            />
                          </TableCell>
                        )}
                        {showCalendarIntegration && (
                          <TableCell>
                            <TextHeading5
                              text={
                                'settings' in displayUser &&
                                displayUser.settings?.calendarIntegration
                                  ? t.calendar.connected
                                  : t.calendar.notConnected
                              }
                            />
                          </TableCell>
                        )}
                        {showActions && (
                          <TableCell onClick={e => e.stopPropagation()}>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'wrap',
                              }}>
                              {actionLabel && onUserAction && (
                                <IconButton
                                  icon={'❌'}
                                  text={actionLabel}
                                  onClick={() =>
                                    setPendingAction({
                                      user: displayUser as User,
                                      action: 'decline',
                                    })
                                  }
                                  backgroundColor={Palette.primaryCherokee}
                                  hoverColor={Palette.white}
                                  color={Palette.almostBlack}
                                />
                              )}
                              {secondaryActionLabel &&
                                onSecondaryUserAction && (
                                  <IconButton
                                    icon={'✅'}
                                    text={secondaryActionLabel}
                                    onClick={() =>
                                      setPendingAction({
                                        user: displayUser as User,
                                        action: 'attend',
                                      })
                                    }
                                    backgroundColor={Palette.primaryCherokee}
                                    hoverColor={Palette.white}
                                    color={Palette.almostBlack}
                                  />
                                )}
                            </div>
                          </TableCell>
                        )}
                      </tr>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '16px 16px',
              }}>
              <PrimaryButton label={t.modals.close} onClick={onClose} />
            </div>
          </ModalContent>
        </ModalOverlay>

        <ConfirmationModal
          isOpen={!!pendingAction}
          onClose={handleCancelAction}
          onConfirm={handleConfirmAction}
          title={
            pendingAction?.action === 'decline'
              ? t.common.confirmMoveToDeclined
              : t.common.confirmMoveToAttending
          }
          message={
            pendingAction?.action === 'decline'
              ? t.common.confirmMoveToDeclinedMessage.replace(
                  '{firstName}',
                  'firstName' in (pendingAction?.user || {})
                    ? (pendingAction?.user as User).firstName || 'this user'
                    : 'this user',
                )
              : t.common.confirmMoveToAttendingMessage.replace(
                  '{firstName}',
                  'firstName' in (pendingAction?.user || {})
                    ? (pendingAction?.user as User).firstName || 'this user'
                    : 'this user',
                )
          }
          confirmLabel={t.modals.confirm}
          cancelLabel={t.modals.cancel}
        />
      </AnimatePresence>
    );
  },
);
